/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, CircularProgress, IconButton, TextField, InputAdornment,
  Button, Typography, Table, TableBody, TableCell, TableRow, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import { api } from '../../../api';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';

const GENDER_LABEL = { M: 'Male', F: 'Female', O: 'Other', U: '—' };

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function GuestPatientsPage() {
  const navigate = useNavigate();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [error, setError] = useState('');
  const searchTimer = useRef(null);
  const PER_PAGE = 20;

  const kb = useListKeyboard({
    rowCount: rows.length,
    onOpen: (i) => { const r = rows[i]; if (r) navigate(`/pharmacy/billing?search=${encodeURIComponent(r.mobile || r.name)}`); },
  });

  const load = useCallback(async (q, pg) => {
    setLoading(true); setError('');
    try {
      writeParams({ search: q, page: pg });
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      const res = await api.pharmacy.listGuestPatients(params);
      setRows(res.rows || []);
      setTotal(res.count || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load guest patients');
    } finally { setLoading(false); }
  }, [writeParams]);

  useEffect(() => { load(search, page); }, [page]); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(val, 1); }, 400);
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3} px={0}>
        {/* Hero Header */}
        <Box sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
            <IconButton onClick={() => navigate('/pharmacy/billing')}
              sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.28)' }, borderRadius: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{
              width: 46, height: 46, borderRadius: 2.5,
              background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PersonSearchIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Pharmacy Billing</Typography>
              <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Guest Patients</Typography>
            </Box>
            <Box sx={{ ml: 1, px: 1.4, py: 0.4, borderRadius: 10, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>{total} total</Typography>
            </Box>
          </Box>
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}

        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>

          {/* Filter bar */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField
              {...kb.searchProps}
              size="small"
              placeholder="Search by name or mobile…  ( / to focus )"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1, minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, fontSize: '0.82rem', background: '#fff',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: TEAL },
                  '&.Mui-focused fieldset': { borderColor: TEAL },
                },
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: TEAL }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading guest patients…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableBody>
                  <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                    {['Name', 'Mobile', 'Gender', 'Age', 'Address', 'Added', 'Action'].map(h => (
                      <TableCell key={h} sx={{
                        fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)',
                        textTransform: 'uppercase', letterSpacing: 0.7,
                        py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none',
                        '&:first-of-type': { pl: 2.5 },
                        '&:last-of-type': { pr: 2.5 },
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>

                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <PersonSearchIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No guest patients found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Guests are added automatically from the billing screen (Others)</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r, i) => (
                    <TableRow key={r.id} {...kb.getRowProps(i)} sx={{
                      background: i % 2 === 0 ? '#fff' : '#fafcff',
                      transition: 'background 0.15s',
                      cursor: 'pointer',
                      '&:hover': { background: TEAL_LIGHT, boxShadow: `inset 0 0 0 1px ${TEAL}22` },
                      ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}),
                    }}>
                      <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{r.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#78909c' }}>{r.mobile || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#546e7a' }}>{GENDER_LABEL[r.gender] || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#546e7a' }}>{r.age ?? '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, maxWidth: 220 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.address || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, pr: 2.5 }}>
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                          onClick={() => navigate(`/pharmacy/billing?search=${encodeURIComponent(r.mobile || r.name)}`)}
                          sx={{
                            fontSize: '0.72rem', color: TEAL, fontWeight: 700, textTransform: 'none',
                            '&:hover': { color: TEAL_DARK, background: 'transparent', textDecoration: 'underline' },
                          }}>
                          Bills
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff',
            }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b> results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page <= 1 ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                  const p = totalPages <= 5 ? idx + 1 : (page <= 3 ? idx + 1 : page - 2 + idx);
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button
                      key={p}
                      size="small"
                      onClick={() => setPage(p)}
                      sx={{
                        minWidth: 32, height: 32, borderRadius: 2, fontSize: '0.78rem', fontWeight: 700,
                        border: `1.5px solid ${p === page ? TEAL : '#e2e8f0'}`,
                        background: p === page ? `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})` : 'transparent',
                        color: p === page ? '#fff !important' : '#78909c',
                        boxShadow: p === page ? `0 2px 8px ${TEAL}44` : 'none',
                        '&:hover': p === page ? { color: '#fff !important' } : { background: TEAL_LIGHT, borderColor: TEAL, color: TEAL },
                      }}>
                      {p}
                    </Button>
                  );
                })}
                <IconButton
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page >= totalPages ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
